import React, { useEffect, useState, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppMode } from './App';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import axios from 'axios';
import './css/Session.css';
import { Typography, CircularProgress } from '@mui/material';
import { useSessionStore, labelMap } from './SessionStore';
import Tooltip from './components/Tooltip';
import Portal from './components/Portal';
import { useDialog } from './components/Modal/DialogContext';
import { useSiteSettings } from './context/SiteSettingsContext';
import { useQueryClient } from "@tanstack/react-query";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

<Tooltip text="Dies ist ein Hinweis">
  <button>Mehr Infos</button>
</Tooltip>

type SessionType = {
  id: number;
  name: string;
  date: string;
  length: number;
  status: string;
  tempStatus?: string; // für temporäre Statusupdates via WebSocket
};

interface SessionProps {
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  setSnackbarSeverity: React.Dispatch<React.SetStateAction<"success" | "warning" | "error">>;
  setOpenSnackbar: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarSticky: React.Dispatch<React.SetStateAction<boolean>>;
}

const Session: React.FC<SessionProps> = ({
  setSnackbarMessage,
  setSnackbarSeverity,
  setOpenSnackbar,
  setSnackbarSticky,
}) => {
  const APPEAR_ANIMATION_MS = 320;
  const DELETE_ANIMATION_MS = 240;
  const mode = useAppMode();
  void mode;
  // Binde sessionStore ein
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const dialog = useDialog();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const sessions = useSessionStore((s) => s.sessions);
  const visibleSessions = sessions.filter(session =>
    mode === "case"
      ? session.entity_type === "case"
      : session.entity_type !== "case"
  );
  const fetchSessions = useSessionStore((s) => s.fetchSessions);
  const selectedIdRef = useRef<number | null>(null);
  const {
    fetchSessionDataById,
    fetchSessionListItem,
    removeSessionFromList,
    clearSelectedSession,
    setSelectedSessionId,
    selectedSessionId,
  } = useSessionStore();
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
  const [sortDescending, setSortDescending] = useState(true);
  const [dataField, setDataField] = useState<any>(null); // JSON-Daten
  const socketRef = useRef<WebSocket | null>(null); // WebSocket Ref
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isPreparingAudioDownload, setIsPreparingAudioDownload] = useState(false);
  const [appearingSessions, setAppearingSessions] = useState<Record<number, true>>({});
  const [deletingSessions, setDeletingSessions] = useState<Record<number, 'pending' | 'collapsing'>>({});
  const longRecordingCheckRef = useRef<Set<number>>(new Set());
  const appearAnimationTimeoutsRef = useRef<Record<number, number>>({});
  const deleteAnimationTimeoutsRef = useRef<Record<number, number>>({});
  const deletingSessionsRef = useRef<Record<number, 'pending' | 'collapsing'>>({});
  const previousSessionIdsRef = useRef<Set<number>>(new Set());
  const hasSeenInitialSessionsRef = useRef(false);
  const [isNameBlurred, setIsNameBlurred] = useState(() => {
    try {
      return localStorage.getItem("sessionNameBlurred") === "1";
    } catch {
      return false;
    }
  });
  
  const triggerRefs = useRef<(HTMLDivElement | null)[]>([]); const handleSessionSelect = async (id: number) => {
    setSelectedSessionId(id); // statt Ref
    selectedIdRef.current = id; // Ref aktualisieren
    return await fetchSessionDataById(id);
  };
  // Dropdown-Referenz entfernt, da nicht mehr benötigt
  const [dropdownTimeout, setDropdownTimeout] = useState<NodeJS.Timeout | null>(null);
  const sessionRefs = useRef<(HTMLLIElement | null)[]>([]);

  const updateDeletingSessions = (
    updater: (prev: Record<number, 'pending' | 'collapsing'>) => Record<number, 'pending' | 'collapsing'>
  ) => {
    setDeletingSessions((prev) => {
      const next = updater(prev);
      deletingSessionsRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    return () => {
      Object.values(appearAnimationTimeoutsRef.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      Object.values(deleteAnimationTimeoutsRef.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
    };
  }, []);

  useEffect(() => {
    const currentIds = new Set(sessions.map((session) => session.id));

    if (!hasSeenInitialSessionsRef.current) {
      hasSeenInitialSessionsRef.current = true;
      previousSessionIdsRef.current = currentIds;
      return;
    }

    const addedIds = Array.from(currentIds).filter((id) => !previousSessionIdsRef.current.has(id));
    previousSessionIdsRef.current = currentIds;

    if (!addedIds.length) return;

    setAppearingSessions((prev) => {
      const next = { ...prev };
      addedIds.forEach((id) => {
        next[id] = true;
      });
      return next;
    });

    addedIds.forEach((id) => {
      if (appearAnimationTimeoutsRef.current[id]) {
        window.clearTimeout(appearAnimationTimeoutsRef.current[id]);
      }
      appearAnimationTimeoutsRef.current[id] = window.setTimeout(() => {
        setAppearingSessions((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        delete appearAnimationTimeoutsRef.current[id];
      }, APPEAR_ANIMATION_MS);
    });
  }, [sessions]);


  useEffect(() => {
    const handleReconnect = () => {
      if (logLevel >= 3) console.log("🌐 [Client] Verbindung wiederhergestellt – fetchSessions() wird ausgeführt.");
      fetchSessions();
    };

    window.addEventListener("online", handleReconnect);

    return () => {
      window.removeEventListener("online", handleReconnect);
    };
  }, []);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // NEU: auch Portal-Menü einbeziehen
      const dropdown = document.querySelector('.custom-dropdown');
      const portalDropdown = document.querySelector('.dropdown-menu');

      if (
        dropdown &&
        !dropdown.contains(target) &&
        portalDropdown &&
        !portalDropdown.contains(target)
      ) {
        setOpenDropdownIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (["ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();

        const currentIndex = visibleSessions.findIndex(s => s.id === selectedSessionId);
        if (currentIndex === -1) return;

        let nextIndex = currentIndex;
        if (e.key === "ArrowDown" && currentIndex < visibleSessions.length - 1) {
          nextIndex = currentIndex + 1;
        }
        if (e.key === "ArrowUp" && currentIndex > 0) {
          nextIndex = currentIndex - 1;
        }

        const nextSession = visibleSessions[nextIndex];
        if (nextSession) {
          handleSessionSelect(nextSession.id); // Daten laden
          sessionRefs.current[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [visibleSessions, selectedSessionId]);
  useEffect(() => {
    const handleScroll = () => {
      setOpenDropdownIndex(null);
    };

    const scrollContainer = document.querySelector('.session-list-wrapper');

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);


  useEffect(() => {
    const now = Date.now();
    const duration = 500; // ms
    const offset = (now % duration) / 1000; // Sekunden
    document.documentElement.style.setProperty('--blink-offset', `-${offset}s`);
  }, []);

  useEffect(() => {
    selectedIdRef.current = selectedSessionId;
  }, [selectedSessionId]);

  useEffect(() => {
    try {
      localStorage.setItem("sessionNameBlurred", isNameBlurred ? "1" : "0");
    } catch {
      // ignore write errors (e.g. private mode)
    }
  }, [isNameBlurred]);



  // Funktion zum Abrufen der Sitzungsdaten
  // !!! Das ist bei vielen daten und vielen anfragen recht ineffizient. Sollte man bei bedarf ändern

  const getWebSocketUrl = () => {
    const token = localStorage.getItem("token");

    if (!token) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ [Client] Kein Token gefunden!");
      return null;
    }

    const isLocal = window.location.hostname === "localhost";
    const socketUrl = isLocal
      ? `ws://localhost:8001/ws/apps/events/?token=${token}`  // Lokale Umgebung
      : `wss://${window.location.hostname}/ws/apps/events/?token=${token}`; // Produktion (SSL gesichert)

    return socketUrl;
  };

  // WebSocket initialisieren
  const setupWebSocket = () => {
    if (socketRef.current) {
      if (!isSiteSettingsLoading && logLevel >= 2) console.warn("⚠️ [Client] WebSocket bereits verbunden.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ [Client] Kein Token gefunden!");
      return;
    }

    const socketUrl = getWebSocketUrl();
    if (!socketUrl) return;

    if (!isSiteSettingsLoading && logLevel >= 3) console.log(`📡 [Client] Verbindung zum WebSocket wird hergestellt: ${socketUrl}`);
    const ws = new WebSocket(socketUrl);
    socketRef.current = ws;

    ws.onopen = () => {
      if (!isSiteSettingsLoading && logLevel >= 3) console.log("✅ [Client] WebSocket erfolgreich verbunden!");
    };

    const runLongRecordingCheck = async (sessionId: number, durationSeconds: number) => {
      if (!durationSeconds || longRecordingCheckRef.current.has(sessionId)) {
        return;
      }

      longRecordingCheckRef.current.add(sessionId);
      try {
        const res = await fetch("/api/subscriptions/check-long-recording-modal/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ duration_seconds: durationSeconds }),
        });
        if (!res.ok) return;
        const modalData = await res.json();
        if (modalData?.show) {
          const durationMinutes = Math.round((modalData.duration_seconds ?? durationSeconds) / 60);
          dialog({
            type: "confirm",
            title: "Lange Aufnahme erkannt",
            message: `Ihre Aufnahme dauerte ${durationMinutes} Minuten. Der aktuelle Tarif ist für bis zu ${modalData.limit_minutes} Minuten ausgelegt. Die Datei wurde vollständig gespeichert.`,
            confirmButton: "Tarifoptionen ansehen",
            cancelButton: "Schließen",
            onConfirm: () => {
              navigate("/settings/planpicker");
            },
          });
        }
      } catch (err) {
        if (!isSiteSettingsLoading && logLevel >= 2) console.warn("Long recording modal check failed:", err);
      }
    };

    ws.onmessage = async (event) => {
      if (!isSiteSettingsLoading && logLevel >= 3) console.log("🔔 [Client] Nachricht empfangen:", event);

      try {
        const data = JSON.parse(event.data);
        if (!isSiteSettingsLoading && logLevel >= 3) console.log("📩 [Client] Verarbeitete Nachricht:", data);

        if (data.type === "session_update") {
          const sessionId = Number(data.session_id);
          const updateEvent = String(data.event || "updated");

          if (Number.isNaN(sessionId)) {
            if (!isSiteSettingsLoading && logLevel >= 2) console.warn("⚠️ [Client] Session-Update ohne gültige session_id, fallback auf fetchSessions().");
            fetchSessions();
            return;
          }

          if (updateEvent === "deleted") {
            if (deletingSessionsRef.current[sessionId]) {
              return;
            }
            removeSessionFromList(sessionId);
            if (selectedIdRef.current === sessionId) {
              clearSelectedSession();
              selectedIdRef.current = null;
            }
          } else if (updateEvent === "created" || updateEvent === "updated") {
            const sessionItem = await fetchSessionListItem(sessionId);
            if (!sessionItem) {
              if (!isSiteSettingsLoading && logLevel >= 2) console.warn(`⚠️ [Client] Session ${sessionId} konnte nicht gezielt geladen werden, fallback auf fetchSessions().`);
              fetchSessions();
              return;
            }
            if (sessionItem.status === "completed") {
              void runLongRecordingCheck(sessionItem.id, sessionItem.length);
            }
            if (selectedIdRef.current === sessionId) {
              await fetchSessionDataById(sessionId);
            }
          } else {
            if (!isSiteSettingsLoading && logLevel >= 2) console.warn(`⚠️ [Client] Unbekanntes Session-Event '${updateEvent}', fallback auf fetchSessions().`);
            fetchSessions();
          }
        }

        if (data.type === "notification") {
          if (!isSiteSettingsLoading && logLevel >= 3) console.log(`📢 [Client] Notification empfangen: ${data.message} (Severity: ${data.severity})`);
          setSnackbarMessage(data.message);
          setSnackbarSeverity(data.severity);
          setSnackbarSticky(false);
          setOpenSnackbar(true);
          queryClient.invalidateQueries({ queryKey: ["usage-status"] });
          queryClient.refetchQueries({ queryKey: ["usage-status"] });
        }

        if (data.type === "session_status") {
          if (!isSiteSettingsLoading && logLevel >= 3) console.log(`📡 Statusupdate für Session ${data.session_id}: ${data.status}`);
          useSessionStore.getState().updateSessionTempStatus(data.session_id, data.status);
        }
      } catch (error) {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ [Client] Fehler beim Parsen der WebSocket-Nachricht:", error);
      }
    };

    ws.onerror = (error) => {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("⚠️ [Client] WebSocket-Fehler:", error);
    };

    ws.onclose = (event) => {
      if (!isSiteSettingsLoading && logLevel >= 2) console.warn(`🔄 [Client] WebSocket getrennt (Code: ${event.code}). Versuche Neuverbindung...`);
      socketRef.current = null;
      setTimeout(setupWebSocket, 2000);
    };
  };

  /** useEffect für WebSocket */
  useEffect(() => {
    if (!isSiteSettingsLoading && logLevel >= 3) console.log("🔄 [Client] useEffect: WebSocket wird eingerichtet...");
    fetchSessions();
    setupWebSocket();

    // Cleanup beim Seitenverlassen (z.B. Tab schließen oder neu laden)
    window.addEventListener("beforeunload", () => {
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.close();
        socketRef.current = null;
      }
    });


    return () => {
      if (!isSiteSettingsLoading && logLevel >= 3) console.log("⏹️ [Client] WebSocket wird geschlossen...");
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.close();
        socketRef.current = null;
      } else {
        // Falls kein aktiver Socket, trotzdem sicherstellen, dass Referenz entfernt wird
        socketRef.current = null;
      }
    };
  }, []);



  const notesToReviewCount = sessions.filter((s) => {
    if (mode === "case") {
      return s.entity_type === "case" && !s.has_documentation;
    }
    return s.entity_type !== "case" && !s.has_documentation;
  }).length;
// Download eines Audio-Files (refactored: use session id, remove all file name logic)

  const handleAudioDownload = async (session_id: number) => {
    setIsPreparingAudioDownload(true);
    try {
      if (!session_id) {
        alert('Keine Sitzung ausgewählt.');
        return;
      }
      try {
        const response = await axios.get(`/api/services/audio-download/${session_id}`, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;

        const disposition = response.headers['content-disposition'];
        let filename = `audio_session_${session_id}.mp3`;

        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match?.[1]) {
            filename = match[1];
          }
        }

        // Erzwinge .mp3-Endung (Browser-Kompatibilität)
        if (!filename.toLowerCase().endsWith('.mp3')) {
          filename = filename.replace(/\.[^/.]+$/, '') + '.mp3';
        }

        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
        
      } catch (error) {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim Herunterladen der Audiodatei:', error);
        alert('Fehler beim Download. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setOpenDropdownIndex(null);
      setIsPreparingAudioDownload(false);
    }
  };

  const handleDelete = (id: number) => {
    if (deletingSessions[id]) return;

    dialog({
      type: 'confirm',
      title: 'Sitzung unwiderruflich löschen?',
      message: 'Möchten Sie diese Sitzung wirklich löschen? Alle zugehörigen Daten werden dauerhaft entfernt und können nicht wiederhergestellt werden.',
      confirmButton: 'Ja, löschen',
      cancelButton: 'Abbrechen',
      danger: true,

      onConfirm: () => {
        updateDeletingSessions((prev) => ({ ...prev, [id]: 'pending' }));
        setOpenDropdownIndex(null);
        axios
          .delete(`/api/apps/delete-session/${id}/`)
          .then((response) => {
            if (response.status === 200) {
              updateDeletingSessions((prev) => ({ ...prev, [id]: 'collapsing' }));
              deleteAnimationTimeoutsRef.current[id] = window.setTimeout(() => {
                removeSessionFromList(id);
                if (selectedIdRef.current === id) {
                  clearSelectedSession();
                  selectedIdRef.current = null;
                }
                updateDeletingSessions((prev) => {
                  const next = { ...prev };
                  delete next[id];
                  return next;
                });
                delete deleteAnimationTimeoutsRef.current[id];
              }, DELETE_ANIMATION_MS);
            } else {
              updateDeletingSessions((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
              });
              alert('Fehler beim Löschen der Session.');
            }
          })
          .catch((error) => {
            updateDeletingSessions((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            });
            if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim Löschen:', error);
            alert('Fehler beim Löschen der Session.');
          });
      },
    });
  };
  return (
    <div id="session-cards" className="lg:w-3/12 order-2 lg:order-1">
     {/* <div
        style={{
          marginBottom: '8px',
          padding: '6px 10px',
          fontSize: '12px',
          borderRadius: '4px',
          backgroundColor:  '#e0f2fe',
          color: '#111827',
        }}
      >
        Sie befinden sich im Mode {mode}
      </div>*/}
      <div id="review__notes" className="review__notes">
        <div className="review__notes__header">
          <div className="review__notes__header__text">
            <h3 className="!tw-text-nowrap">
              {mode === "case" ? "Neue Berichte" : "Neue Sitzungen"}
            </h3>

            <p className={`${notesToReviewCount === 0 ? 'zero_sessions' : ''}`}>{notesToReviewCount}</p>
            <div className="review__notes__header__toggle">
              <Tooltip text={isNameBlurred ? "Namen anzeigen" : "Namen ausblenden"}>
                <button
                  type="button"
                  className="session__name-visibility-toggle"
                  onClick={() => setIsNameBlurred((prev) => !prev)}
                  aria-pressed={isNameBlurred}
                  aria-label={isNameBlurred ? "Namen anzeigen" : "Namen ausblenden"}
                >
                  {isNameBlurred ? (
                    <VisibilityOffOutlinedIcon style={{ fontSize: 22 }} />
                  ) : (
                    <VisibilityOutlinedIcon style={{ fontSize: 22 }} />
                  )}
                </button>
              </Tooltip>
            </div>

          </div>
          {/*<div className="input__container !tw-w-full md:!tw-w-1/2" style={{ pointerEvents: 'auto', opacity: 1 }}>
            <div className="input__container__div !tw-bg-white tw-rounded-md border tw-w-full  md:tw-ml-auto">
              <input className="input__container__div__input !tw-bg-white !tw-pl-8" type="text" placeholder="Search by client name" inputMode="text" />
            </div>
          </div>*/}
        </div>
        <div className="review__notes__sort">
          <div className="lines"></div>
          <button onClick={() => setSortDescending(prev => !prev)}>
            <FilterListOutlinedIcon
              style={{
                transform: sortDescending ? 'rotate(0deg)' : 'rotate(180deg)',
                marginRight: '2px',
                marginLeft: '6px',
                fontSize: '20px',
                color: 'inherit'
              }}
            />
            <p className="upload__text text-purple-700">Sortiert nach Datum</p>
          </button>
        </div>
        <div className="session-list-wrapper" style={{ overflowY: 'auto', maxHeight: '65vh', margin: '0 auto' }}>
          <ul className="session-list tw-grid tw-gap-4 tw-grid-cols-1 " style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {[...visibleSessions].sort((a, b) => sortDescending
              ? new Date(b.date).getTime() - new Date(a.date).getTime()
              : new Date(a.date).getTime() - new Date(b.date).getTime()
            ).map((session, index) => {
              const isEntityCase = session.entity_type === "case";
              const isProcessing = session.tempStatus !== 'completed';
              const isInputDocuments =session.input_source === 'documents';
              const isAppearing = !!appearingSessions[session.id];
              const deleteState = deletingSessions[session.id];
              const isDeleting = !!deleteState;
              const isCollapsing = deleteState === 'collapsing';

              return (
                <li
                  ref={el => sessionRefs.current[index] = el}

                  key={session.id}
                  data-id={session.id}
                  onClick={async () => {
                    if (isDeleting) return;
                    const sessionData = await handleSessionSelect(Number(session.id));
                    if (sessionData) {
                      setDataField(sessionData.data || null);
                    }
                  }}
className={`session__card session__grab ${selectedSessionId === session.id ? 'session__card--active' : ''} ${isAppearing ? 'session__card--appearing' : ''}`}
style={{
  position: 'relative',
  overflow: 'hidden',
  maxHeight: isCollapsing ? 0 : 500,
  opacity: isCollapsing ? 0 : 1,
  transform: isCollapsing ? 'scaleY(0.96)' : 'scaleY(1)',
  marginTop: isCollapsing ? 0 : undefined,
  marginBottom: isCollapsing ? 0 : undefined,
  paddingTop: isCollapsing ? 0 : undefined,
  paddingBottom: isCollapsing ? 0 : undefined,
  pointerEvents: isDeleting ? 'none' : 'auto',
  transition: `max-height ${DELETE_ANIMATION_MS}ms ease, opacity ${DELETE_ANIMATION_MS}ms ease, transform ${DELETE_ANIMATION_MS}ms ease, margin ${DELETE_ANIMATION_MS}ms ease, padding ${DELETE_ANIMATION_MS}ms ease`,
  backgroundColor:
    selectedSessionId === session.id
      ? (!isEntityCase ? '#faeed5' : '')
      : '',
}}                >
                  {isDeleting && (
                    <div className={`session__delete-overlay ${isCollapsing ? 'session__delete-overlay--collapsing' : ''}`}>
                      <span>Wird gel&ouml;scht...</span>
                    </div>
                  )}
                  <div className="session__card__details">
                    {/* Header mit Infos */}
                    <div className="session__card__details__header">
                      <p className="tw-m-0 tw-font-bold tw-text-[14px]">
                        {isEntityCase ? "Bericht" : "Sitzung"}
                      </p>

                      <div className="session session_viewed">
                        {new Date(session.date).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
                        <span> • </span>
                        {!isEntityCase && (
                          <>
                            <span>{Math.floor(session.length / 60)}:{String(session.length % 60).padStart(2, '0')} Min</span>
                            <span> • </span>
                          </>
                        )}
                        <span>
                          vor {Math.ceil((Date.now() - new Date(session.date).getTime()) / (1000 * 60 * 60 * 24))} Tagen
                        </span>
                      </div>
                    </div>

                    {/* Rechts: Icon & Dropdown */}
                    <div className="session__card__details__right">
                      <div className="viewed_p">
                        <div className="tw-flex tw-items-center tw-gap-1">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            color={isEntityCase ? "#E7A555" : "#32768D"}
                          >
                            <path d="M2.20146 15.1664C1.96146 15.1664 1.72812 15.1064 1.52812 14.9864C1.08145 14.7264 0.828125 14.2064 0.828125 13.5598V5.98643C0.828125 4.56643 1.98145 3.41309 3.40145 3.41309H8.58813C10.0081 3.41309 11.1615 4.56643 11.1615 5.98643V13.5598C11.1615 14.2064 10.9081 14.7198 10.4615 14.9864C10.0148 15.2464 9.44146 15.2197 8.8748 14.9064L6.2548 13.4464C6.12813 13.3731 5.86145 13.3731 5.73478 13.4464L3.11479 14.9064C2.80812 15.0797 2.49479 15.1664 2.20146 15.1664Z" fill="currentColor"></path>
                          </svg>
                          <span
                            className={`!tw-text-[12px] !tw-font-normal tw-relative top-[2px] ${
                              isEntityCase ? 'tw-text-primary-500 !text-[#E7A555]' : '!tw-text-primary-500'
                            }`}
                          >
                            {isEntityCase
                              ? (session.has_documentation ? "Erfasst" : "Offener Bericht")
                              : (session.has_documentation ? "Erfasst" : "Neue Sitzung")}
                          </span>
                        </div>
                      </div>

                      {/* Dropdown */}

                      <div className="custom-dropdown" style={{ position: 'relative' }}>
                        <div
                          ref={(el) => (triggerRefs.current[index] = el)}
                          className="dropdown-trigger-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            const el = triggerRefs.current[index];
                            if (el) {
                              const rect = el.getBoundingClientRect();

                              const menuWidth = 220; // entspricht minWidth des Dropdowns
                              const padding = 8;

                              // Wunschposition (Desktop-Verhalten)
                              let left = rect.left + window.scrollX - 85;

                              // Viewport-Clamping: nur korrigieren, wenn nötig
                              left = Math.max(
                                padding,
                                Math.min(left, window.innerWidth - menuWidth - padding)
                              );

                              setDropdownPosition({
                                top: rect.bottom + window.scrollY - 4,
                                left,
                              });
                            }
                            setOpenDropdownIndex(openDropdownIndex === index ? null : index);
                          }} style={{ cursor: 'pointer', padding: '4px', marginLeft: '16px' }} // Abstand via margin
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <line x1="4" y1="8" x2="20" y2="8" />
                            <line x1="4" y1="16" x2="20" y2="16" />
                          </svg>
                        </div>

                        {openDropdownIndex === index && (
                          <Portal>
                            <div
                              className="dropdown-menu"
                              style={{
                                position: 'absolute',
                                top: dropdownPosition.top,
                                left: dropdownPosition.left,
                                zIndex: 9999,
                                backgroundColor: '#fff',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                borderRadius: '6px',
                                minWidth: '220px',
                                padding: '3px',
                              }}
                              onMouseEnter={() => {
                                if (dropdownTimeout) clearTimeout(dropdownTimeout); // Timeout abbrechen, wenn Maus drüber
                              }}
                              onMouseLeave={() => {
                                const timeout = setTimeout(() => {
                                  setOpenDropdownIndex(null); // Nach 3 Sek. schließen
                                }, 2000);
                                setDropdownTimeout(timeout);
                              }}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isPreparingAudioDownload && !session.audio_deleted && !isInputDocuments) {
                                    handleAudioDownload(session.id);
                                  }
                                }}
                                disabled={isPreparingAudioDownload || session.audio_deleted || isInputDocuments}
                                className="dropdown-item"
                                style={{
                                  padding: '8px 16px',
                                  cursor: (isPreparingAudioDownload || session.audio_deleted || isInputDocuments) ? 'default' : 'pointer',
                                  fontSize: '0.9rem',
                                  color: '#333',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  opacity: (isPreparingAudioDownload || session.audio_deleted || isInputDocuments) ? 0.6 : 1,
                                  width: '100%',
                                  boxSizing: 'border-box',
                                }}
                                onMouseEnter={(e) => {
                                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f2f2f2';
                                }}
                                onMouseLeave={(e) => {
                                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                                }}
                              >
                                {isInputDocuments ? (
                                  <>
                                    <DownloadOutlinedIcon style={{ color: '#999', fontSize: '18px' }} />
                                    Kein Audio verfügbar
                                  </>
                                ) : session.audio_deleted ? (
                                  <>
                                    <DownloadOutlinedIcon style={{ color: '#999', fontSize: '18px' }} />
                                    Audio nicht verfügbar
                                  </>
                                ) : isPreparingAudioDownload ? (
                                  <>
                                    <CircularProgress size={16} style={{ color: '#32768d' }} />
                                    Audio wird vorbereitet…
                                  </>
                                ) : (
                                  <>
                                    <DownloadOutlinedIcon style={{ color: '#40937a', fontSize: '18px' }} />
                                    Audio herunterladen
                                  </>
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdownIndex(null);
                                  handleDelete(session.id);
                                }}
                                className="dropdown-item"
                                style={{
                                  padding: '8px 16px ',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  color: '#333',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  width: '100%',
                                  boxSizing: 'border-box',
                                }}
                                onMouseEnter={(e) => {
                                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f2f2f2';
                                }}
                                onMouseLeave={(e) => {
                                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                                }}
                              >
                                <DeleteOutlineIcon style={{ color: 'red', fontSize: '18px' }} />
                                Löschen
                              </button>
                            </div></Portal>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="session__card__name">
                    <span
                      className={`session__card__name__text ${isNameBlurred ? "session__card__name__text--blurred" : ""}`}
                      style={!session.name?.trim() ? { color: "#bbb", fontStyle: "italic", opacity: 0.7 } : undefined}
                    >
                      {session.name?.trim() ? session.name : "Unbenannt"}
                    </span>
                  </p>

                  {isProcessing && (
                    <div className="progress">
                      <div className={`progress__bar ${session.tempStatus === "waiting"
                          ? "progress__bar--default"
                          : session.tempStatus === "failed"
                            ? "progress__bar--error"
                            : "progress__bar--success"
                        }`} style={{ width: '100%' }}>
                        <div className="progress__bar__stripes"></div>
                      </div>
                      <div className="progress__status">
                        <div className="tw-w-full progress__status mobile__pending">
                          <div className="status note">
                            <p>{labelMap[session?.tempStatus || "unknown"]}</p>
                            {/*<p className="!tw-text-[#8F8F8F]">about 4 minutes left</p>*/}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Session;
